import React, { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { Arrow } from 'icons/untitledui';
import routesConfig from 'constants/routesConfig.json';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import * as Styled from 'shiftNotes/styles/NotesStack.style';

interface DocumentTranslation {
  id?: string;
  title?: string;
  name?: string;
  parent: { id: string; use_source_url?: boolean };
  language?: string;
  page_numbers: number[];
  source_url?: string;
}

interface NoteCardGiveAnswerSourcesProps {
  documentTranslations?: DocumentTranslation[];
  noteId: string;
  reflexId: string;
  stepId: string;
}

export default function NoteCardGiveAnswerSources({
  documentTranslations,
  noteId,
  reflexId,
  stepId,
}: NoteCardGiveAnswerSourcesProps): JSX.Element | null {
  const { t } = useTranslation('pageShiftNotes');
  const history = useHistory();
  const location = useLocation();
  const shiftNotesCapture = usePosthogCapture();

  const documents = documentTranslations ?? [];
  if (documents.length === 0) return null;

  const captureSourceClicked = (sourceType: 'internal' | 'external') => {
    shiftNotesCapture.noteReflexGiveAnswerSourceClicked({
      shift_note_id: noteId,
      reflex_id: reflexId,
      step_id: stepId,
      source_type: sourceType,
    });
  };

  const handleDocumentClick = (document: DocumentTranslation) => {
    captureSourceClicked('internal');
    const query: string[] = [];
    if (document.language) {
      query.push(`language=${encodeURIComponent(document.language)}`);
    }
    if (document.page_numbers.length > 0) {
      query.push(`pages=${document.page_numbers.join(',')}`);
    }
    const qs = query.length ? `?${query.join('&')}` : '';

    history.push({
      pathname: `${routesConfig.DOCUMENT_PAGE}/${document.parent.id}`,
      search: qs,
      state: { from: `${location.pathname}${location.search}` },
    });
  };

  return (
    <>
      <Styled.StepSectionLabel>
        {t('instantReflex.sources')}
      </Styled.StepSectionLabel>
      <Styled.StepActionColumn>
        {documents.map((document, index) => {
          const label =
            document.title || document.name || `Document ${index + 1}`;
          const isExternal = Boolean(
            document.parent?.use_source_url && document.source_url
          );

          if (isExternal) {
            return (
              <Styled.StepActionPill
                key={document.id || index}
                as="a"
                href={document.source_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  captureSourceClicked('external');
                }}
              >
                {label}
                <Arrow.ArrowUpRight />
              </Styled.StepActionPill>
            );
          }

          return (
            <Styled.StepActionPill
              key={document.id || index}
              type="button"
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handleDocumentClick(document);
              }}
            >
              {label}
            </Styled.StepActionPill>
          );
        })}
      </Styled.StepActionColumn>
    </>
  );
}
